<template>
  <div>
    <QuestionLabel
      :number="question.questionNumber"
      :text="question.text"
    ></QuestionLabel>
    <div class="pb-6">
      <div v-if="dense" class="mb-16" key="dense">
        <div class="mt-3 mb-8">
          <table class="w-100">
            <thead>
              <th class="pl-0 pb-4"></th>
              <template v-if="data[0].groupedBy.length > 0">
                <th
                  v-for="agg in data"
                  :key="agg.groupedBy[0].value"
                  class="vertical-align-bottom text-center text-capitalize"
                  style="width: 100px"
                >
                  {{ agg.groupedBy[0].text }}
                </th>
              </template>

              <th
                v-else
                class="vertical-align-bottom text-center text-capitalize"
                style="width: 100px"
              >
                Percentage
              </th>
            </thead>
            <tr v-for="child in question.children" :key="child.key">
              <td class="w-100 py-4 d-flex">
                <v-chip
                  v-if="child.questionNumber && child.questionNumber.length > 0"
                  color="secondary"
                  class="text-capitalize mr-4 flex-shrink-0"
                >
                  {{ child.questionNumber }}
                </v-chip>
                <div class="d-flex align-center">
                  <survey-question-text
                    :text="child.text"
                  ></survey-question-text>
                </div>
              </td>

              <template v-if="data[0].groupedBy.length > 0">
                <td
                  class="text-right text-no-wrap"
                  v-for="(agg, i) in data"
                  :key="agg.groupedBy[0].value"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on">
                        <comparison-button
                          :value="getRate(child.key, i)"
                        ></comparison-button>
                      </div>
                    </template>
                    <span>{{ agg.groupedBy[0].text }}</span>
                  </v-tooltip>
                </td>
              </template>
              <td v-else class="text-right text-no-wrap">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <comparison-button
                        :value="getRate(child.key, 0)"
                      ></comparison-button>
                    </div>
                  </template>
                  <span>Rate of Agreement</span>
                </v-tooltip>
              </td>
            </tr>
          </table>
        </div>
      </div>
      <template v-else>
        <div v-for="child in question.children" :key="child.key" class="mb-16">
          <div class="mt-3 mb-8">
            <div
              class="font-weight-bold d-flex text-left mb-4"
              v-if="smallScreen"
            >
              <v-chip
                v-if="child.questionNumber && child.questionNumber.length > 0"
                color="secondary"
                class="text-capitalize mr-2 flex-shrink-0"
                >{{ child.questionNumber }}</v-chip
              >
              <survey-question-text
                :text="child.text"
                class="mt-1"
              ></survey-question-text>
            </div>
            <table>
              <thead>
                <th class="pl-0 pb-4">
                  <div
                    class="font-weight-bold d-flex text-left"
                    v-if="!smallScreen"
                  >
                    <v-chip
                      v-if="
                        child.questionNumber && child.questionNumber.length > 0
                      "
                      color="secondary"
                      class="text-capitalize mr-2 flex-shrink-0"
                      >{{ child.questionNumber }}</v-chip
                    >
                    <survey-question-text
                      :text="child.text"
                      class="mt-1"
                    ></survey-question-text>
                  </div>
                </th>
                <th
                  class="vertical-align-bottom text-right text-capitalize text-no-wrap"
                >
                  Agree Percentage
                </th>
              </thead>

              <template v-if="data[0].groupedBy.length > 0">
                <tr v-for="(agg, i) in data" :key="agg.groupedBy[0].value">
                  <td class="w-100 pl-10">{{ agg.groupedBy[0].text }}</td>
                  <td class="text-right text-no-wrap">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on">
                          <comparison-button
                            class="my-1"
                            :value="getRate(child.key, i)"
                          ></comparison-button>
                        </div>
                      </template>
                      <span>{{ agg.groupedBy[0].text }}</span>
                    </v-tooltip>
                  </td>
                </tr>
              </template>
              <tr v-else>
                <td class="w-100 pl-10">All Responses</td>
                <td class="text-right text-no-wrap">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on">
                        <comparison-button
                          class="my-1"
                          :value="getRate(child.key, 0)"
                        ></comparison-button>
                      </div>
                    </template>
                    <span>All Responses</span>
                  </v-tooltip>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
$blueFill: rgba(101, 146, 230, 0.15);
$greyFill: rgba(0, 0, 0, 0.05);

table {
  border-collapse: collapse; /* Remove cell spacing */

  thead {
    th {
      vertical-align: bottom;
      text-align: center;
      padding-top: 0.5rem;
      border-radius: 0.5rem 0.5rem 0 0;
      padding: 0.5rem 1rem 0.25rem 1rem;

      &:nth-child(even) {
        background: $greyFill;
      }
    }
  }

  th,
  td {
    padding: 0.25rem 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0);
    text-align: left;
  }

  tr,
  tfoot {
    &:nth-child(odd) {
      th,
      td {
        &:nth-child(even) {
          background: $greyFill;
        }
      }
    }
    &:nth-child(even) {
      th,
      td {
        &:nth-child(even) {
          background: $greyFill;
        }
      }
    }
  }

  tr {
    &:hover {
      background: rgba(0, 0, 0, 0.15) !important;

      td {
        border-color: rgba(0, 0, 0, 0.25);
      }
    }

    &:nth-child(even) {
      background: $blueFill;
    }
  }

  tfoot {
    td {
      border-radius: 0 0 0.5rem 0.5rem;
      padding: 0.5rem;
    }
  }
}
</style>

<script>
import numeral from "numeral";

import ComparisonButton from "./QuestionReport.AgreeAnalysis.ComparisonButton.vue";
import QuestionLabel from "./QuestionReport.Label.vue";
import SurveyQuestionText from "./Survey.Question.Text.vue";

export default {
  components: { ComparisonButton, QuestionLabel, SurveyQuestionText },
  filters: {
    percentage(val) {
      return numeral(val).format("0.00%");
    },
  },
  data: () => ({}),
  props: {
    survey: Object,
    question: Object,
    data: Array,
    dense: Boolean,
  },
  computed: {
    smallScreen() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  methods: {
    questionData(questionKey, aggIndex) {
      var results = this.data[aggIndex].results.filter(
        (x) => x.key === questionKey
      );
      if (results.length == 0) return null;
      else return results[0];
    },
    getRate(questionKey, aggIndex) {
      let data = this.questionData(questionKey, aggIndex);
      if (!data) return 0;

      var numerator = data.counts.reduce((val, count) => {
        let option = this.question.options.find((x) => x.value == count.key);
        if (option && option.weight > 0) {
          val += count.value;
        }

        return val;
      }, 0);

      var denominator = data.responses;

      if (!numerator) return 0;
      else return numerator / denominator;
    },
  },
};
</script>
